import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import {
    ClockCounterClockwiseIcon,
    CalendarBlankIcon,
    HardDrivesIcon,
    InfoIcon,
} from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import type { RetentionSettings } from '@/types/admin-settings';
import { formatFileSize } from '@/utils/storage';
import { StorageBreakdown, StorageSlice } from './StorageBreakdown';
import { StorageTabSkeleton } from './StorageTabSkeleton';
import { StorageStatCard } from './StorageStatCard';
import type { OrgStorage } from '@/types/system';

const RETENTION_CACHE_KEY = 'admin-retention';
const STORAGE_CACHE_KEY = 'org-storage';

function parseDailyCronTime(cron: string): string | null {
    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 5) return null;
    const [minute, hour, dom, month, dow] = parts;
    const isDaily = dom === '*' && month === '*' && dow === '*';
    if (!isDaily) return null;
    const min = Number.parseInt(minute, 10);
    const hr = Number.parseInt(hour, 10);
    if (Number.isNaN(min) || Number.isNaN(hr)) return null;
    return `${hr}:${String(min).padStart(2, '0')}`;
}

function buildStorageSlices(
    data: OrgStorage,
    t: (key: string) => string,
): StorageSlice[] {
    return [
        {
            key: 'cameras',
            label: t('organization.storageCameras'),
            value: data.cameraFootage.diskUsageBytes,
            color: 'hsl(var(--chart-1))',
        },
        {
            key: 'videos',
            label: t('organization.storageFiles'),
            value: data.videoStorageBytes,
            color: 'hsl(var(--chart-2))',
        },
        {
            key: 'other',
            label: t('organization.storageOther'),
            value: data.otherStorageBytes,
            color: 'hsl(var(--chart-3))',
        },
    ];
}

export function StorageTab() {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const {
        data: retentionData,
        isLoading: retentionLoading,
        error: retentionError,
    } = useSWR<RetentionSettings>(
        RETENTION_CACHE_KEY,
        () => AdminSettingsService.getRetentionSettings(),
        { revalidateOnFocus: false },
    );
    const { data: storageData, isLoading: storageLoading } = useSWR<OrgStorage>(
        STORAGE_CACHE_KEY,
        () => AdminSettingsService.getOrgStorage(),
        { revalidateOnFocus: false },
    );

    if (retentionLoading || storageLoading) {
        return <StorageTabSkeleton />;
    }

    if (retentionError || !retentionData) {
        return (
            <div className="border border-danger-300 rounded-[5px] p-4 text-danger-200 text-sm">
                {t('storage.loadFailed')}
            </div>
        );
    }

    const dailyTime = parseDailyCronTime(retentionData.cleanupCron);
    const scheduleLabel = dailyTime
        ? t('storage.dailyAt', { time: dailyTime })
        : retentionData.cleanupCron;
    const storageSlices = storageData ? buildStorageSlices(storageData, t) : [];

    return (
        <div className="flex flex-col gap-6 pt-14 pb-24">
            <div className="flex items-start gap-2 p-3 rounded-md border border-grey-600 bg-grey-700/40">
                <InfoIcon className="size-4 text-grey-300 shrink-0 mt-0.5" />
                <p className="text-sm text-grey-200">
                    {t('storage.readOnlyNotice')}
                </p>
            </div>

            <div
                className="grid gap-4"
                style={{
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                }}
            >
                <StorageStatCard
                    icon={ClockCounterClockwiseIcon}
                    label={t('storage.retentionWindow')}
                    value={t('storage.daysValue', {
                        count: retentionData.dataRetentionDays,
                    })}
                    description={t('storage.retentionDescription')}
                />
                <StorageStatCard
                    icon={CalendarBlankIcon}
                    label={t('storage.cleanupSchedule')}
                    value={scheduleLabel}
                    description={t('storage.scheduleDescription')}
                />
                {storageData?.diskTotalBytes ? (
                    <StorageStatCard
                        icon={HardDrivesIcon}
                        label={t('storage.diskFree')}
                        value={t('storage.diskFreeValue', {
                            free: formatFileSize(storageData.diskFreeBytes),
                            total: formatFileSize(storageData.diskTotalBytes),
                        })}
                        description={t('storage.diskFreeDescription')}
                    />
                ) : null}
            </div>
            <StorageBreakdown
                slices={storageSlices}
                label={t('organization.storage')}
            />
        </div>
    );
}
